import { create, el, resizer } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { View } from "@dhx/ts-common/view";
import { DataCollection, DataEvents, TreeCollection } from "@dhx/ts-data";
import { ComposeLayer } from "./ComposeLayer";
import { Legend } from "./Legend";
import {
	ChartEvents,
	IChart,
	IChartConfig,
	IChartSeries,
	IComposeLayer,
	IScaleConfig,
	IScales,
	ISeria,
} from "./types";
import scaleTypes from "./scales/index";
import chartTypes from "./series/index";
import Stacker from "./series/Stacker";
import { getDefaultColor, getScales } from "./helpers/common";
import { Tooltip } from "./Tooltip";
import { uid } from "@dhx/ts-common/core";

export class Chart extends View implements IChart {
	public data: DataCollection | TreeCollection;
	public events: IEventSystem<DataEvents | ChartEvents>;
	public config: IChartConfig;

	private _layers: IComposeLayer;
	private _series: IChartSeries;
	private _scales: IScales;

	private _tooltip: Tooltip;

	private _globalHTMLHandlers: any;

	// using zero values ensure that widget will not attempt to render self in the hidden state
	private _width = 0;
	private _height = 0;

	private _left = 0;
	private _top = 0;
	constructor(node: HTMLElement | string, config: IChartConfig = {}) {
		super(null, config);

		const dataConfig: any = {};
		if (config.maxPoints) {
			dataConfig.approximate = {
				value: config.series.map(a => a.value),
				maxNum: config.maxPoints,
			};
		}

		if (Array.isArray(config.data)) {
			this.events = new EventSystem<DataEvents>(this);
			this.data =
				config.type === "treeMap"
					? new TreeCollection(dataConfig, this.events)
					: new DataCollection(dataConfig, this.events);
			this.data.parse(config.data);
		} else if (config.data && config.data.events) {
			this.data = config.data;
			this.events = this.data.events as IEventSystem<DataEvents | ChartEvents>;
			this.events.context = this;
		} else {
			this.events = new EventSystem<DataEvents>(this);
			this.data =
				config.type === "treeMap"
					? new TreeCollection(dataConfig, this.events)
					: new DataCollection(dataConfig, this.events);
		}

		this._globalHTMLHandlers = {
			onmousemove: e => {
				const { left, top, bottom, right } = this._layers.getSizes();
				const { pageX, pageY } = e;
				const rects = this.getRootView().node.el.getBoundingClientRect();
				this._left = rects.left + window.pageXOffset;
				this._top = rects.top + window.pageYOffset;

				const x = pageX - left - this._left;
				const y = pageY - top - this._top;
				if (x >= 0 && x <= this._width - right - left && y >= 0 && y <= this._height - bottom - top) {
					this.events.fire(ChartEvents.chartMouseMove, [x, y, this._left + left, this._top + top]);
				} else {
					this.events.fire(ChartEvents.chartMouseLeave);
				}
			},
			onmouseleave: () => this.events.fire(ChartEvents.chartMouseLeave),
		};

		this._layers = new ComposeLayer();
		this.setConfig(config);
		this._initEvents();
		this._tooltip = new Tooltip(this);

		const render = () => {
			if (!this.data.getLength()) {
				return el("div");
			}

			const getChartAriaLabel = config => {
				/*  Full example (without symbols of new line):
				    Type chart "Title". X/Y Scale Name: bottom axis from Min to Max. Scale Name: left axis Name from Min to Max.
				    Series: seria A, seria B, seria C, ...
				 */
				const scales = this._scales;
				const { series } = config;
				let { type } = config;

				if (!type) {
					// scatter chart
					type = series && series.length && series[0] ? series[0].type || "" : "";
				}

				let label = `${type || ""} chart.`;

				const getAxisName = cfg => (cfg._isXDirection ? "X" : "Y");
				const getAxisTitle = cfg => cfg.title || cfg.text || cfg.value || "";
				const getAxisMin = cfg => (cfg.steps && cfg.steps.length ? cfg.steps[0] : cfg.min || 0);
				const getAxisMax = cfg =>
					cfg.steps && cfg.steps.length
						? cfg.steps[cfg.steps.length - 1]
						: cfg.max || cfg.maxPoints || "max";
				const getRadialAxisValue = (attr, point) => point[attr];

				if (scales) {
					Object.keys(scales).forEach(direction => {
						const scale = scales[direction];
						const cfg = scale._axis || scale.config;

						if (direction === "radial") {
							const xData = scale._data._order;
							label += ` X scale ${getAxisTitle(
								scale.config
							)}: ${direction} axis from ${getRadialAxisValue(
								scale.config.value,
								xData[0]
							)} to ${getRadialAxisValue(scale.config.value, xData[xData.length - 1])}.`;

							label += ` ${getAxisName(scale)} scale: axis from ${getAxisMin(
								cfg
							)} to ${getAxisMax(cfg)}.`;
						} else {
							label += ` ${getAxisName(scale)} scale ${getAxisTitle(
								scale.config
							)}: ${direction} axis from ${getAxisMin(cfg)} to ${getAxisMax(cfg)}.`;
						}
					});
				}

				if (series && series.length) {
					label += " Series:";
					series.forEach((seria, index) => {
						label += ` ${
							type === "pie" || type === "pie3D" || type === "donut" ? seria.text : seria.value
						}`;
						label += index === series.length - 1 ? "." : ",";
					});
				}

				return label;
			};
			const getChartAriaAttrs = config => ({
				"aria-label": getChartAriaLabel(config),
			});
			const content = [
				resizer((x, y) => {
					this._width = x;
					this._height = y || 400; // if height is not provided, use default value

					const view = this.getRootView();
					if (view && view.node && view.node.el) {
						const rects = view.node.el.getBoundingClientRect();
						this._left = rects.left + window.pageXOffset;
						this._top = rects.top + window.pageYOffset;
					}
					this.events.fire(ChartEvents.resize, [this._width, this._height]);
					this.paint();
					if (!document.querySelector(".dhx_widget.dhx_chart") && this._tooltip) {
						this._tooltip.destructor();
					}
				}),
			];

			if (this._width && this._height) {
				content.push(this._layers.toVDOM(this._width, this._height));
			}

			return el(
				".dhx_widget.dhx_chart",
				{
					class: config.css ? config.css : "",
					onmousemove: this._globalHTMLHandlers.onmousemove,
					onmouseleave: this._globalHTMLHandlers.onmouseleave,
					...getChartAriaAttrs(config),
					tabIndex: 0,
				},
				content
			);
		};
		const view = create({
			render,
			hooks: {
				didMount: (vm: any) => {
					if (vm && vm.node && vm.node.parent && vm.node.parent.el) {
						this._width = vm.node.parent.el.offsetWidth;
						this._height = vm.node.parent.el.offsetHeight || 400;
						this.paint();
					}
				},
			},
		});
		this.mount(node, view);
	}

	getSeries(key: string): ISeria {
		return this._series[key];
	}

	eachSeries(handler: (serie: ISeria) => any): any[] {
		const result = [];
		for (const key in this._series) {
			result.push(handler.call(this, this._series[key]));
		}
		return result;
	}

	destructor() {
		this._tooltip.destructor();
		this.events.clear();
		this.unmount();
	}

	setConfig(config: IChartConfig): void {
		this.config = config;
		this._layers.clear();
		this._series = {};
		this._scales = {};
		let min;
		// let baseLine;

		if (config.scales) {
			for (const key in config.scales) {
				const scale = { ...config.scales[key] };
				if (config.scales[key].min !== undefined) {
					min = config.scales[key].min;
				}
				scale.type = scale.type || this._detectScaleType(scale, key);
				if (config.scales.radial && key !== "radial") {
					scale.hidden = true;
				}
				this._setScale(scale, key);
			}
		}

		const stack = new Stacker();
		this._layers.add(stack);

		config.series.forEach((cfg, ind) => {
			if (cfg.baseLine !== undefined && cfg.baseLine < min) {
				cfg.baseLine = undefined;
			}
			const serieConfig = { ...cfg };
			serieConfig.type = serieConfig.type || config.type;

			switch (serieConfig.type) {
				case "bar":
				case "xbar":
				case "area":
				case "splineArea":
					if (!serieConfig.color) serieConfig.color = serieConfig.fill || getDefaultColor(ind);
					if (!serieConfig.fill) serieConfig.fill = serieConfig.color || getDefaultColor(ind);
					break;
				case "treeMap":
					serieConfig.legendType = config.legend.type || "groupName";
					config.legend.treeSeries.map((serie, index) => {
						serie.active = serie.active ?? true;
						serie.id = serie.id ?? uid();
						if (!serie.color)
							serie.color = getDefaultColor(index, serieConfig.legendType === "range");
					});
					serieConfig.treeSeries = config.legend.treeSeries;
					break;
				case "radar":
				case "scatter":
					if (!serieConfig.pointColor)
						serieConfig.pointColor = serieConfig.color || getDefaultColor(ind);
					break;
			}

			const chartFactory = chartTypes[serieConfig.type];
			if (serieConfig.barWidth || this.config.barWidth) {
				serieConfig.barWidth = serieConfig.barWidth || this.config.barWidth;
			}
			const chart = new chartFactory(this.data, serieConfig, this.events);

			const scales = getScales(config.scales);
			const chartScales =
				scales.length > 1 && scales[0] !== "radial"
					? scales
					: scales[0] === "radial"
					? scales
					: ["bottom", "left"];
			chartScales.forEach(type => {
				const scale = this._scales[type];
				if (!scale) {
					return;
				}
				chart.addScale(type, scale);
				if (!serieConfig.stacked) {
					scale.add(chart);
				} else {
					scale.add(stack);
				}
			});
			this._series[chart.id] = chart;

			if (serieConfig.stacked) {
				stack.add(chart);
			} else {
				this._layers.add(chart);
			}
		});

		if (config.legend) {
			const legendConfig = { ...config.legend };
			if (legendConfig.series) {
				legendConfig.$seriesInfo = legendConfig.series.map(id => this._series[id]);
			}

			const legend = new Legend(this.data, legendConfig, this.events);
			this._layers.add(legend);
		}
		this.paint();
	}

	private _setScale(config: IScaleConfig, position: string): void {
		const scale = new scaleTypes[config.type](this.data, config, position);

		if (scale.config.type !== "radial") {
			this._layers.add(scale.scaleGrid());
		}
		this._layers.add(scale);

		this._scales[position] = scale;
	}

	private _detectScaleType(config, key) {
		if (key === "radial") {
			return key;
		}
		if (config.text) {
			return "text";
		}
		return "numeric";
	}

	private _initEvents(): void {
		// hide/show series on legend click
		this.events.on(
			ChartEvents.toggleSeries,
			(id, pieLike) => {
				if (this.config.type === "treeMap") {
					Object.values(this._series)[0].toggle(id);
					this.paint();
				} else if (pieLike) {
					const serie = this._series[Object.keys(this._series)[0]];
					if (serie) {
						serie.toggle(id);
						this.paint();
					}
				} else if (this._series[id]) {
					this._series[id].toggle();
					this.paint();
				}
			},
			this
		);

		// repaint on data change
		this.events.on(DataEvents.change, () => this.paint());
	}
}
